import { ReactNode, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { handleSignOut } from 'lib/utils/amplify';
import { validateJWT } from 'lib/utils';
import { logout } from 'lib/actions';
import { refreshToken } from 'utils/auth';
import jwtDecode from 'jwt-decode';
import { Navigate, useLocation } from 'react-router-dom';
import { useIsAccountActivationRequired } from '../../../../hooks';
import {
    PAYMENT_MAKE_PAYMENT,
    PAYMENT_SEND_PAYMENT,
    PAYMENT_STANDING_ORDER,
} from '../../../../constants/shared/routes';

const forbiddenRoutesForReadOnly = new Set([
    PAYMENT_MAKE_PAYMENT,
    PAYMENT_SEND_PAYMENT,
    PAYMENT_STANDING_ORDER,
]);

const REFRESH_BEFORE_EXPIRY_MS = 60 * 1000;

interface WithAuthProps {
    children?: ReactNode;
}

const WithAuth = ({ children }: WithAuthProps) => {
    const storedToken = localStorage.getItem('accessToken');
    const isAuthorized = validateJWT(storedToken);
    const isAccountActivationRequired = useIsAccountActivationRequired();
    const dispatch = useDispatch();
    const { pathname } = useLocation();

    const handleLogout = () => {
        dispatch(logout() as any);
        handleSignOut();
    };

    useEffect(() => {
        if (!storedToken) return;
        try {
            const { exp } = jwtDecode<{ exp: number }>(storedToken);
            const now = Date.now();
            const expirationTime = exp * 1000;
            const timeUntilRefresh = Math.max(
                expirationTime - now - REFRESH_BEFORE_EXPIRY_MS,
                0,
            );

            const timeoutId = setTimeout(() => {
                void refreshToken(dispatch);
            }, timeUntilRefresh);

            return () => clearTimeout(timeoutId);
        } catch (err) {
            console.log('Invalid token:', err);
            dispatch(logout() as any);
        }
    }, [dispatch, storedToken]);

    if (!isAuthorized) {
        handleLogout();
        return <Navigate to="/auth" />;
    }

    if (
        isAccountActivationRequired &&
        forbiddenRoutesForReadOnly.has(pathname)
    ) {
        return <Navigate to="/" />;
    }

    return <>{children}</>;
};

export default WithAuth;
